import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('wa_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const orgId = payload.organizationId || payload.orgId;
        const role = payload.role || 'ORG_ADMIN';
        const hasPaidRegistration = payload.hasPaidRegistration === true;
        const organizationName = payload.organizationName || localStorage.getItem('wa_org_name') || '';
        const phoneNumber = payload.phoneNumber || localStorage.getItem('wa_phone') || '';

        localStorage.setItem('wa_role', role);
        localStorage.setItem('wa_has_paid', String(hasPaidRegistration));
        if (organizationName) localStorage.setItem('wa_org_name', organizationName);
        if (orgId) localStorage.setItem('wa_org_id', orgId);

        setUserRole(role);
        setUser({
          email: payload.sub,
          role,
          name: localStorage.getItem('wa_name') || payload.sub,
          organizationId: orgId,
          organizationName,
          hasPaidRegistration,
          phoneNumber,
        });
      } catch (error) {
        console.error('Error parsing token:', error);
        localStorage.clear();
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    const data = response.data;

    const hasPaid = data.hasPaidRegistration === true;
    const fullName = (data.firstName + ' ' + data.lastName).trim() || email;
    const orgName = data.organizationName || data.organization?.name || '';

    localStorage.setItem('wa_token', data.token);
    localStorage.setItem('wa_name', fullName);
    localStorage.setItem('wa_role', data.role);
    localStorage.setItem('wa_has_paid', String(hasPaid));
    localStorage.setItem('wa_org_name', orgName);
    if (data.organizationId) localStorage.setItem('wa_org_id', data.organizationId);

    setToken(data.token);
    setUserRole(data.role);
    setUser({
      email: data.email,
      role: data.role,
      name: fullName,
      organizationId: data.organizationId,
      organizationName: orgName,
      hasPaidRegistration: hasPaid,
    });

    return data;
  };

  const register = async (userData) => {
    const response = await api.post('/api/auth/register', {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      organizationName: userData.organizationName,
    });

    const data = response.data;
    const fullName = (data.firstName + ' ' + data.lastName).trim() || data.email;
    const orgName = data.organizationName || userData.organizationName || '';

    if (data.token) {
      localStorage.setItem('wa_token', data.token);
      localStorage.setItem('wa_name', fullName);
      localStorage.setItem('wa_role', data.role);
      localStorage.setItem('wa_has_paid', 'false');
      localStorage.setItem('wa_org_name', orgName);
      localStorage.setItem('wa_phone', userData.phoneNumber);
      if (data.organizationId) localStorage.setItem('wa_org_id', data.organizationId);

      setToken(data.token);
      setUserRole(data.role);
      setUser({
        email: data.email,
        role: data.role,
        name: fullName,
        organizationId: data.organizationId,
        organizationName: orgName,
        hasPaidRegistration: false,
        phoneNumber: userData.phoneNumber,
      });
    }

    return data;
  };

  const verifyOtp = async (code) => {
    const storedToken = localStorage.getItem('wa_token');
    const response = await api.post(
      '/api/auth/otp/verify',
      { code },
      { headers: { Authorization: `Bearer ${storedToken}` } }
    );
    return response.data;
  };

  const resendOtp = async () => {
    const storedToken = localStorage.getItem('wa_token');
    const response = await api.post(
      '/api/auth/otp/resend',
      {},
      { headers: { Authorization: `Bearer ${storedToken}` } }
    );
    return response.data;
  };

  const forgotPassword = async (emailOrPhone) => {
    const response = await api.post('/api/auth/password/forgot', { emailOrPhone });
    return response.data;
  };

  const verifyResetOtp = async (emailOrPhone, code) => {
    const response = await api.post('/api/auth/password/verify-otp', { emailOrPhone, code });
    return response.data;
  };

  const resetPassword = async (email, newPassword, confirmPassword) => {
    const response = await api.post('/api/auth/password/reset', {
      email,
      newPassword,
      confirmPassword,
    });
    return response.data;
  };

  const logout = () => {
    localStorage.clear();
    setToken(null);
    setUser(null);
    setUserRole(null);
  };

  const checkRegistrationStatus = async () => {
    try {
      const response = await api.get('/api/payments/registration-status');
      const hasPaid = response.data.paid === true;
      if (user) setUser({ ...user, hasPaidRegistration: hasPaid });
      localStorage.setItem('wa_has_paid', String(hasPaid));
      return hasPaid;
    } catch (error) {
      console.error('Error checking registration status:', error);
      return false;
    }
  };

  const isSuperAdmin = userRole === 'SUPER_ADMIN';
  const isOrgAdmin = userRole === 'ORG_ADMIN';

  return (
    <AuthContext.Provider value={{
      user,
      userRole,
      isSuperAdmin,
      isOrgAdmin,
      token,
      loading,
      login,
      register,
      logout,
      verifyOtp,
      resendOtp,
      forgotPassword,
      verifyResetOtp,
      resetPassword,
      checkRegistrationStatus,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}